import { useEffect, useMemo, useRef, useState } from 'react';
import debounce from 'lodash/debounce';
import * as Form from '@radix-ui/react-form';
import { faMagnifyingGlass } from '@fortawesome/pro-solid-svg-icons';
import type { IDropdownInputOptions } from '@/_components/dropdown-input/dropdown-input';
import { DropdownInput } from '@/_components/dropdown-input/dropdown-input';
import { TextInput } from '@/_components/text-input/text-input';
import type { ENUM_DELIVERY_TYPE } from '@/_features/deliveries/constants';
import { TYPE_DELIVERY_OPTIONS } from '@/_features/deliveries/constants';
import { MONITOR_CODE_SEARCH_DEBOUNCE } from '../../constants';
import { normalizeMonitorCode } from '../../hooks/use-monitor-filters';
import styles from './filters.module.scss';

interface MonitorFiltersProps {
  cityId?: string;
  code?: string;
  deliveryType?: ENUM_DELIVERY_TYPE;
  citiesOptions: IDropdownInputOptions[];
  loadingCities: boolean;
  onChangeCity: (cityId?: string) => void;
  onChangeCode: (code: string) => void;
  onChangeDeliveryType: (deliveryType?: ENUM_DELIVERY_TYPE) => void;
}

export function MonitorFilters({
  cityId,
  code = '',
  deliveryType,
  citiesOptions,
  loadingCities,
  onChangeCity,
  onChangeCode,
  onChangeDeliveryType,
}: MonitorFiltersProps) {
  const [codeInput, setCodeInput] = useState(code);
  const onChangeCodeRef = useRef(onChangeCode);
  onChangeCodeRef.current = onChangeCode;

  const debouncedChangeCode = useMemo(
    () =>
      debounce(
        (value: string) => onChangeCodeRef.current(value),
        MONITOR_CODE_SEARCH_DEBOUNCE,
      ),
    [],
  );

  useEffect(() => () => debouncedChangeCode.cancel(), [debouncedChangeCode]);

  useEffect(() => {
    setCodeInput(current =>
      normalizeMonitorCode(current) === code ? current : code,
    );
  }, [code]);

  function handleChangeCode(value: string) {
    setCodeInput(value);
    debouncedChangeCode(value);
  }

  return (
    <Form.Root
      className={styles.filters}
      onSubmit={event => event.preventDefault()}
    >
      <div className={styles.fields}>
        <DropdownInput
          name="cityId"
          clearable
          label="Município"
          containerClassName={styles.field}
          options={citiesOptions}
          loading={loadingCities}
          value={cityId || ''}
          search
          onChange={value => {
            if (typeof value === 'string') {
              onChangeCity(value || undefined);
            }
          }}
        />

        <DropdownInput
          name="deliveryType"
          clearable
          label="Tipo de entrega"
          placeholder="Todos"
          containerClassName={styles.field}
          options={TYPE_DELIVERY_OPTIONS}
          value={deliveryType || ''}
          onChange={value => {
            if (typeof value === 'string') {
              onChangeDeliveryType((value as ENUM_DELIVERY_TYPE) || undefined);
            }
          }}
        />

        <TextInput
          name="code"
          aria-label="Buscar pelo código do pedido"
          label="Código do pedido"
          containerClassName={styles.field}
          icon={faMagnifyingGlass}
          placeholder="ABC12D"
          value={codeInput}
          style={{ textTransform: 'uppercase' }}
          onChange={event => handleChangeCode(event.target.value)}
        />
      </div>
    </Form.Root>
  );
}
